import LoadingSpinner from '$tablet-app/src/app/components/LoadingSpinner';
import Spacer from '$tablet-app/src/app/components/Spacer';
import { Button } from '@akin-home/buttons/native';
import { generateWeek } from '@akin-home/calendar';
import {
  CalendarItemUnion,
  useListCalendarItems,
} from '@akin-home/calendar/combined';
import {
  endOfWeekSunday,
  getWeekText,
  startOfWeekMonday,
} from '@akin-home/dates';
import { useHabitatID } from '@akin-home/habitats/react';
import { Text } from '@akin-home/text/native';
import { colors } from '@akin-home/theme';
import { addWeeks } from 'date-fns';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { CalendarScreenModeButtons } from '../CalendarScreenModeButtons';
import CalendarSheet from '../CalendarSheet';
import { CalendarWeekItem } from './CalendarWeekItem';

type Props = {
  onPressItem?(item: CalendarItemUnion): void;
};

export const CalendarWeekView = (props: Props) => {
  const [date, setDate] = useState(new Date());

  const habitatID = useHabitatID();

  const { items, isLoading } = useListCalendarItems({
    params: {
      habitatID,
      filter: {
        from: startOfWeekMonday(date).toISOString(),
        to: endOfWeekSunday(date).toISOString(),
      },
    },
  });

  const week = useMemo(() => generateWeek(date, items), [items, date]);

  const goToNextWeek = () => setDate(addWeeks(date, 1));
  const goToPreviousWeek = () => setDate(addWeeks(date, -1));

  const week_display_text = getWeekText(date);

  return (
    <CalendarSheet>
      <Container>
        <TopRow>
          <CalendarScreenModeButtons onPressToday={setDate} />

          <WeekSwitcher>
            {isLoading ? <LoadingSpinner color={colors.primary[70]} /> : null}

            <Button
              icon="chevron-left"
              variant="secondary"
              onPress={goToPreviousWeek}
            />
            <Text variant="h2" weight="semibold">
              {week_display_text}
            </Text>
            <Button
              icon="chevron-right"
              variant="secondary"
              onPress={goToNextWeek}
            />
          </WeekSwitcher>
        </TopRow>

        <Week>
          {week.map((day, idx) => (
            <React.Fragment key={day.date_text}>
              <DayColumn>
                <DayName variant="h2" weight="semibold">
                  {day.day_name}
                </DayName>

                <DateText variant="h3" weight="semibold">
                  {day.date_text}
                </DateText>

                <ItemList
                  data={day.items}
                  keyExtractor={(item) => (item as CalendarItemUnion).id}
                  renderItem={({ item }) => (
                    <CalendarWeekItem
                      item={item as CalendarItemUnion}
                      onPress={props.onPressItem}
                    />
                  )}
                />
              </DayColumn>

              {idx < week.length - 1 ? (
                <Spacer height="100%" width="1px" color="lightgrey" />
              ) : null}
            </React.Fragment>
          ))}
        </Week>
      </Container>
    </CalendarSheet>
  );
};

const Container = styled.View`
  flex: 1;
`;

const TopRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  padding-bottom: 0;
`;

const WeekSwitcher = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 20px;
`;

const Week = styled.View`
  flex: 1;
  flex-direction: row;
  width: 100%;
  gap: 15px;

  padding: 20px;
  padding-right: 0;
  padding-bottom: 0;
`;

const DayName = styled(Text)`
  font-size: 22px;
`;

const DateText = styled(Text)`
  font-size: 20px;
`;

const DayColumn = styled.View`
  flex: 1;
`;

const ItemCard = styled.TouchableOpacity<{ color: string }>`
  background-color: ${({ color }) => color};
  padding: 16px;
  border-radius: 15px;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const ItemList = styled.FlatList`
  padding-bottom: 30px;
`;
